/**
 * 👤 ENHANCED USER CARD - Better layout with photo placeholders and clear action buttons
 */

import React from 'react';
import { generateUserReference } from '@/utils/userReferenceGenerator';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  LinearProgress,
  Checkbox,
} from '@mui/material';
import {
  Person,
  Visibility,
  Edit,
  CheckCircle,
  Block,
  Message,
  MoreVert,
  LocationOn,
  Phone,
  Email,
  Star,
  Delete,
  Notifications,
} from '@mui/icons-material';

import { VerificationStatusBadge, getVerificationStatusFromUser } from './VerificationStatusBadge';
import { SimpleVerificationBadge, getSimpleVerificationStatus } from './SimpleVerificationBadge';
import type { AdminUser } from '@/services/adminApiService';

interface EnhancedUserCardProps {
  user: AdminUser;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  onViewProfile: (userId: string) => void;
  onApprove: (userId: string) => void;
  onReject: (userId: string) => void;
  onMessage: (userId: string) => void;
  onEdit?: (userId: string) => void;
  onSuspend?: (userId: string) => void;
  onDelete?: (userId: string) => void;
  onEditQuota?: (userId: string) => void;
  onManageNotifications?: (userId: string) => void;
  showDetailedVerification?: boolean; // If true, shows percentages (for Approval Pipeline), if false shows simple statuses (for User Directory)
}

export const EnhancedUserCard: React.FC<EnhancedUserCardProps> = ({
  user,
  selected = false,
  onSelect,
  onViewProfile,
  onApprove,
  onReject,
  onMessage,
  onEdit,
  onSuspend,
  onDelete,
  onEditQuota,
  onManageNotifications,
  showDetailedVerification = false // Default to simple view (User Directory)
}) => {
  const [actionMenuAnchor, setActionMenuAnchor] = React.useState<null | HTMLElement>(null);

  const verification = getVerificationStatusFromUser(user);
  const simpleVerification = getSimpleVerificationStatus(user);
  
  const handleActionClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
  };

  const getDisplayName = (user: AdminUser): string => {
    // Use real name from NIN verification if available
    if (user.driverVerification?.identity?.firstName && user.driverVerification?.identity?.lastName) {
      return `${user.driverVerification.identity.firstName} ${user.driverVerification.identity.lastName}`;
    }
    
    // Use provided name
    if (user.name && user.name !== 'undefined' && !user.name.startsWith('User ')) {
      return user.name;
    }
    
    // Extract name from email if no proper name
    const emailName = user.email.split('@')[0];
    const formattedName = emailName.split('.').map(part => 
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join(' ');
    
    return formattedName;
  };

  const getRoleCount = (user: AdminUser) => {
    return user.roles?.length || 1;
  };

  const getAdditionalRoles = (user: AdminUser) => {
    if (!user.roles || user.roles.length <= 1) return null;
    return user.roles.filter(role => role !== user.userType).join(', ');
  };

  // Map role to user-friendly label
  const getRoleLabel = (role: string): string => {
    const roleMap: Record<string, string> = {
      'user': 'Renter',
      'customer': 'Renter',
      'driver': 'Driver',
      'admin': 'Admin',
      'host': 'Host',
      'renter': 'Renter'
    };
    return roleMap[role.toLowerCase()] || role;
  };

  // Get all user roles including inferred ones
  const getUserRoles = (user: AdminUser): string[] => {
    const roles: string[] = [];

    // Debug logging for first few users
    if (Math.random() < 0.05) { // Log ~5% of users to avoid spam
      // User role data tracked here
    }

    // Primary role from API
    if (user.role) {
      roles.push(getRoleLabel(user.role));
    } else if (user.userType) {
      roles.push(getRoleLabel(user.userType));
    }

    // Check for additional roles array
    if (user.roles && user.roles.length > 0) {
      user.roles.forEach(role => {
        const label = getRoleLabel(role);
        if (!roles.includes(label)) {
          roles.push(label);
        }
      });
    }

    // Infer Host role if user has vehicles
    if ((user as any).vehicleCount > 0 || (user as any).hasVehicles) {
      if (!roles.includes('Host')) {
        roles.push('Host');
      }
    }

    // Infer Driver role if verified driver
    if (user.driverVerification && user.driverVerification.status === 'verified_driver') {
      if (!roles.includes('Driver')) {
        roles.push('Driver');
      }
    }

    return roles.length > 0 ? roles : ['Renter']; // Default to Renter if no roles found
  };

  const displayName = getDisplayName(user);
  const additionalRoles = getAdditionalRoles(user);
  const canApprove = verification.status === 'complete' || verification.status === 'pending_review';

  return (
    <TableRow key={user.id} hover>
      {/* Selection Checkbox */}
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={(e) => onSelect?.(e.target.checked)}
          inputProps={{ 'aria-labelledby': `user-${user.id}` }}
        />
      </TableCell>
      
      {/* User Info */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              width: 48, 
              height: 48,
              bgcolor: verification.status === 'verified_driver' ? 'success.main' : 'primary.main'
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {displayName}
              </Typography>
              <Chip
                label={generateUserReference(user.id, user.userType as any)}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  borderColor: 'primary.main',
                  color: 'primary.main'
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Email sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            {user.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Phone sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {user.phone}
                </Typography>
              </Box>
            )}
            {user.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {user.location}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>
      
      {/* Type & Roles */}
      <TableCell>
        <Stack spacing={0.5} alignItems="flex-start">
          {(() => {
            const userRoles = getUserRoles(user);
            const isMultiRole = userRoles.length > 1;

            return (
              <>
                {/* Display all roles as chips */}
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {userRoles.join(', ')}
                </Typography>

                {/* Multi-role indicator */}
                {isMultiRole && (
                  <Chip
                    icon={<Star />}
                    label={`Multi-Role (${userRoles.length})`}
                    color="secondary"
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      '& .MuiChip-icon': { fontSize: 14 }
                    }}
                  />
                )}
              </>
            );
          })()}
        </Stack>
      </TableCell>
      
      {/* Verification Status */}
      <TableCell>
        <Stack spacing={1} alignItems="flex-start">
          {showDetailedVerification ? (
            // DETAILED VIEW for Approval Pipeline - shows percentages and progress
            <>
              <VerificationStatusBadge
                status={verification.status}
                completionPercentage={verification.percentage}
                type={verification.type}
                showPercentage={true}
                size="small"
              />

              {/* Progress bar for incomplete items */}
              {verification.percentage < 100 && (
                <Box sx={{ width: '100%', maxWidth: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={verification.percentage}
                    color={verification.percentage > 75 ? 'success' : verification.percentage > 50 ? 'warning' : 'error'}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                </Box>
              )}

              {/* Document count and photo link if available */}
              {user.driverVerification?.documents && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {user.driverVerification.documents.length} documents uploaded
                  </Typography>
                  {(user as any).identity?.identityPhotoUrl && (
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', cursor: 'pointer' }}>
                      📷 View submitted photos
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            // SIMPLE VIEW for User Directory - high-level status only
            <SimpleVerificationBadge
              status={simpleVerification}
              size="small"
            />
          )}
        </Stack>
      </TableCell>
      
      {/* Overall Status */}
      <TableCell>
        <Stack spacing={1} alignItems="flex-start">
          <Chip
            label={user.isVerified ? 'Verified User' : 'Pending Verification'}
            color={user.isVerified ? 'success' : 'warning'}
            size="small"
            variant={user.isVerified ? 'filled' : 'outlined'}
          />
          {user.createdAt && (
            <Typography variant="caption" color="text.secondary">
              Joined: {new Date(user.createdAt).toLocaleDateString()}
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Referral Tier */}
      <TableCell>
        <Chip
          label={(user as any).referralTier === 0 ? 'Admin' : `User T${(user as any).referralTier || 1}`}
          color={(user as any).referralTier === 0 ? 'secondary' : 'default'}
          size="small"
          variant="outlined"
        />
      </TableCell>

      {/* Referral Quota */}
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {(user as any).referralQuota === -1 ? '∞' : (user as any).referralQuota || 5}
        </Typography>
      </TableCell>

      {/* Referrals Used */}
      <TableCell>
        <Typography variant="body2">
          {(user as any).referralsUsed || 0}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Quick Action Buttons */}
          <Tooltip title="View Profile">
            <IconButton
              size="small"
              onClick={() => onViewProfile(user.id)}
              color="primary"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          
          {canApprove && (
            <Tooltip title="Approve User">
              <IconButton
                size="small"
                onClick={() => onApprove(user.id)}
                color="success"
              >
                <CheckCircle />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={handleActionClick}
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Action Menu */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionClose}
        >
          <MenuItem onClick={() => { onViewProfile(user.id); handleActionClose(); }}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Profile</ListItemText>
          </MenuItem>

          {onEdit && (
            <MenuItem onClick={() => { onEdit(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit User</ListItemText>
            </MenuItem>
          )}

          {onEditQuota && (
            <MenuItem onClick={() => { onEditQuota(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <Person fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Edit Referral Quota</ListItemText>
            </MenuItem>
          )}

          {canApprove && (
            <MenuItem onClick={() => { onApprove(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <CheckCircle fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approve</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={() => { onMessage(user.id); handleActionClose(); }}>
            <ListItemIcon>
              <Message fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send Message</ListItemText>
          </MenuItem>

          {onManageNotifications && (
            <MenuItem onClick={() => { onManageNotifications(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <Notifications fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText>Notification Preferences</ListItemText>
            </MenuItem>
          )}

          {onSuspend && (
            <MenuItem onClick={() => { onSuspend(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <Block fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>Suspend User</ListItemText>
            </MenuItem>
          )}

          {onDelete && (
            <MenuItem onClick={() => { onDelete(user.id); handleActionClose(); }}>
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete User (Testing)</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={() => { onReject(user.id); handleActionClose(); }}>
            <ListItemIcon>
              <Block fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Block User</ListItemText>
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};